import { Fragment, useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronDown, ChevronUp, ChevronRight, ChevronLeft, ChevronsLeft, ChevronsRight,
  ArrowUpDown,
  Loader2, AlertTriangle, CheckCircle2,
  Activity, RefreshCw, Cloud, Truck, AlertCircle, Flag,
} from 'lucide-react'
import {
  getAuspostBundles, updateAuspostItemReview, approveAuspostBundle,
  getAuspostSyncStatus, syncAuspostNow, getAuspostWorkerStatus,
  getAdminClients,
} from '../../lib/api'
import { ToastContainer, useToast } from '../../components/Toast'
import AuspostTabs from './AuspostTabs'
import { addDays, businessToday } from '../../lib/dates'


// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt$(v) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency', currency: 'AUD', minimumFractionDigits: 2,
  }).format(v ?? 0)
}
function fmtDate(s) {
  if (!s) return '—'
  const d = new Date(s)
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
  })
}
function fmtRelative(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  if (Number.isNaN(diffMs)) return '—'
  const s = Math.round(diffMs / 1000)
  if (s < 5)     return 'just now'
  if (s < 60)    return `${s}s ago`
  if (s < 3600)  return `${Math.round(s / 60)}m ago`
  if (s < 86400) return `${Math.round(s / 3600)}h ago`
  return `${Math.round(s / 86400)}d ago`
}

function defaultPeriod() {
  // Also seeds syncPeriod further down, not just the list filter — so this
  // wants the business date, matching what the AusPost ingest stamps.
  const today = businessToday()
  return { from: addDays(today, -30), to: today }
}


// ─── Worker heartbeat card ───────────────────────────────────────────────────

function WorkerStatusCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['auspost', 'worker-status'],
    queryFn:  getAuspostWorkerStatus,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })
  if (isLoading || !data) {
    return (
      <div className="card px-4 py-3 flex items-center gap-3 text-slate-400 text-sm">
        <Loader2 size={16} className="animate-spin" /> Loading worker status…
      </div>
    )
  }
  const config = {
    ok:        { dot: 'bg-emerald-500', label: 'Healthy',   cls: 'border-emerald-200 bg-emerald-50/30' },
    stale:     { dot: 'bg-amber-500',   label: 'Stale',     cls: 'border-amber-200 bg-amber-50/40'    },
    error:     { dot: 'bg-red-500',     label: 'Errored',   cls: 'border-red-200 bg-red-50/40'         },
    never_run: { dot: 'bg-slate-400',   label: 'Never run', cls: 'border-slate-200 bg-slate-50/50'     },
    unknown:   { dot: 'bg-slate-300',   label: 'Unknown',   cls: 'border-slate-200 bg-slate-50/50'     },
  }
  const c = config[data.status] || config.unknown
  const intervalMin = Math.round((data.poll_interval_seconds || 900) / 60)
  const totals = data.totals_24h || {}
  return (
    <div className={`card border ${c.cls}`}>
      <div className="px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
        <div className="flex items-center gap-2 min-w-[160px]">
          <span className={`inline-block w-2.5 h-2.5 rounded-full ${c.dot}`} />
          <div>
            <div className="font-semibold text-slate-800 leading-tight flex items-center gap-1.5">
              <Activity size={14} /> Worker: {c.label}
            </div>
            <div className="text-xs text-slate-500 leading-tight">
              auspost-charges-poll · every {intervalMin}m
            </div>
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Last poll</div>
          <div className="text-slate-700">
            {data.last_run ? fmtRelative(data.last_run.started_at) : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Last success</div>
          <div className="text-slate-700">
            {data.last_success ? fmtRelative(data.last_success.completed_at || data.last_success.started_at) : '—'}
          </div>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider">Last 24h</div>
          <div className="text-slate-700 tabular-nums">
            <span className="text-slate-500">{totals.cycles || 0} cycles · </span>
            <span className="text-emerald-700 font-medium">{totals.parsed || 0}</span>
            <span className="text-slate-400 mx-1">new ·</span>
            <span className="text-slate-500">{totals.duplicate || 0} updated</span>
            {(totals.errors || 0) > 0 && (
              <>
                <span className="text-slate-400 mx-1">·</span>
                <span className="text-red-700 font-medium">{totals.errors} err</span>
              </>
            )}
          </div>
        </div>
      </div>
      {data.status === 'error' && data.last_run?.error_message && (
        <div className="px-4 py-2.5 border-t border-red-200 bg-red-50/60 text-xs text-red-800 flex items-start gap-2">
          <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
          <div><span className="font-semibold">Last error:</span> {data.last_run.error_message}</div>
        </div>
      )}
      {data.status === 'stale' && (
        <div className="px-4 py-2.5 border-t border-amber-200 bg-amber-50/60 text-xs text-amber-800 flex items-start gap-2">
          <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
          <div>No poll in over {Math.round((data.poll_interval_seconds || 900) * 2 / 60)}m — check DigitalOcean → Workers.</div>
        </div>
      )}
    </div>
  )
}


// ─── Sync card (manual pull) ─────────────────────────────────────────────────

function SyncCard({ period, setPeriod }) {
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()
  const { data: status } = useQuery({
    queryKey: ['auspost', 'sync-status'],
    queryFn:  getAuspostSyncStatus,
    refetchInterval: 60_000,
  })
  const configured = status?.configured
  const syncMut = useMutation({
    mutationFn: () => syncAuspostNow({ period_start: period.from, period_end: period.to }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auspost'] })
      const ab = data.auto_bill || {}
      const billedBits = [
        ab.charged_ap   ? `${ab.charged_ap} auto-charged AP`   : null,
        ab.charged_ss   ? `${ab.charged_ss} kept SS`           : null,
        ab.pending_review ? `${ab.pending_review} need review` : null,
      ].filter(Boolean).join(' · ')
      addToast({
        type: 'success',
        message: `${data.items_inserted} new · ${data.items_updated} updated · ${data.orders_seen} orders walked` + (billedBits ? `\nAuto-bill: ${billedBits}` : ''),
        duration: 7000,
      })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || err.message || 'Sync failed',
      duration: 8000,
    }),
  })
  return (
    <div className={`card border ${configured ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200 bg-slate-50/40'}`}>
      <ToastContainer toasts={toasts} onClose={removeToast} />
      <div className="px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
        <div className="flex items-center gap-2 min-w-[180px]">
          <Cloud size={16} className={configured ? 'text-emerald-600' : 'text-slate-400'} />
          <div>
            <div className="font-semibold text-slate-800 leading-tight">AusPost API</div>
            <div className="text-xs text-slate-500 leading-tight">
              {configured ? 'Connected — manual sync available' : 'AUSPOST_* env vars not set'}
            </div>
          </div>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">From</label>
            <input
              type="date"
              value={period.from}
              onChange={(e) => setPeriod({ ...period, from: e.target.value })}
              className="input text-sm py-1 w-auto"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 uppercase tracking-wider mb-1">To</label>
            <input
              type="date"
              value={period.to}
              onChange={(e) => setPeriod({ ...period, to: e.target.value })}
              className="input text-sm py-1 w-auto"
            />
          </div>
          <button
            onClick={() => syncMut.mutate()}
            disabled={!configured || syncMut.isPending}
            className="btn-primary text-sm h-[34px]"
            title={configured ? 'Pull AP API for this period' : 'Configure AUSPOST_* env first'}
          >
            {syncMut.isPending
              ? <><Loader2 size={14} className="animate-spin" /> Syncing…</>
              : <><RefreshCw size={14} /> Sync now</>}
          </button>
        </div>
      </div>
    </div>
  )
}


// ─── Table-header filters ───────────────────────────────────────────────────
//
// Date range + client filter that live on the table itself, independent of
// the sync card's pull window. Defaults to last 30 days; "All clients"
// initially. Compact one-row layout that sits inside the table card so it
// reads as part of the table, not as another global filter.

function TableFilters({ tableFilter, setTableFilter, clients }) {
  const update = (patch) => setTableFilter({ ...tableFilter, ...patch })
  const reset = () => setTableFilter({ ...defaultPeriod(), client_id: '' })
  const sortedClients = useMemo(
    () => [...(clients || [])].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [clients],
  )
  const dirty =
    tableFilter.client_id !== '' ||
    tableFilter.from !== defaultPeriod().from ||
    tableFilter.to   !== defaultPeriod().to

  return (
    <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50/60">
      <div>
        <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-1">From</label>
        <input
          type="date"
          value={tableFilter.from}
          onChange={(e) => update({ from: e.target.value })}
          className="input text-xs py-1 w-[140px]"
        />
      </div>
      <div>
        <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-1">To</label>
        <input
          type="date"
          value={tableFilter.to}
          onChange={(e) => update({ to: e.target.value })}
          className="input text-xs py-1 w-[140px]"
        />
      </div>
      <div className="flex-1 min-w-[180px]">
        <label className="block text-[10px] text-slate-500 uppercase tracking-wider mb-1">Client</label>
        <select
          value={tableFilter.client_id}
          onChange={(e) => update({ client_id: e.target.value })}
          className="input text-xs py-1 w-full"
        >
          <option value="">All clients</option>
          {sortedClients.map((c) => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>
      </div>
      {dirty && (
        <button
          onClick={reset}
          className="text-xs px-3 py-1.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100"
          title="Reset to last 30 days, all clients"
        >
          Reset
        </button>
      )}
    </div>
  )
}


// ─── Filter chips ────────────────────────────────────────────────────────────

function FilterChips({ showOnly, setShowOnly, summary }) {
  const chips = [
    { key: 'discrepancies', label: 'Discrepancies', count: summary?.total_discrepancies },
    { key: 'unmatched',     label: 'Unmatched',     count: summary ? summary.total_orders - summary.total_matched : null },
    { key: 'approved',      label: 'Approved',      count: null },
    { key: 'all',           label: 'All orders',    count: summary?.total_orders },
  ]
  return (
    <div className="flex items-center gap-2 flex-wrap">
      {chips.map(c => (
        <button
          key={c.key}
          onClick={() => setShowOnly(c.key)}
          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            showOnly === c.key
              ? 'bg-blue-100 text-blue-700'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          {c.label}
          {typeof c.count === 'number' && (
            <span className="ml-1.5 tabular-nums opacity-70">({c.count})</span>
          )}
        </button>
      ))}
    </div>
  )
}


// ─── Stat tiles ──────────────────────────────────────────────────────────────

function StatTiles({ summary }) {
  if (!summary) return null
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
      <div className="card p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">SS Orders</p>
        <p className="text-2xl font-bold text-slate-800">{summary.total_orders}</p>
      </div>
      <div className="card p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Matched to AP</p>
        <p className="text-2xl font-bold text-emerald-600">{summary.total_matched}</p>
      </div>
      <div className="card p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Discrepancies</p>
        <p className={`text-2xl font-bold ${summary.total_discrepancies > 0 ? 'text-amber-600' : 'text-slate-800'}`}>
          {summary.total_discrepancies}
        </p>
      </div>
      <div className="card p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Pending review</p>
        <p className={`text-2xl font-bold ${summary.total_pending_review > 0 ? 'text-red-600' : 'text-slate-800'}`}>
          {summary.total_pending_review}
        </p>
      </div>
      <div className="card p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Variance Σ</p>
        <p className={`text-2xl font-bold ${
          summary.variance_grand_total > 0.01 ? 'text-red-600' :
          summary.variance_grand_total < -0.01 ? 'text-emerald-600' :
          'text-slate-800'
        }`}>
          {fmt$(summary.variance_grand_total)}
        </p>
      </div>
    </div>
  )
}


// ─── Bundle row + item rows ──────────────────────────────────────────────────

function reviewBadge(status) {
  const map = {
    pending:     { bg: 'bg-slate-100',  fg: 'text-slate-600', label: 'Pending' },
    approved:    { bg: 'bg-emerald-50', fg: 'text-emerald-700', label: 'Approved' },
    disputed:    { bg: 'bg-amber-50',   fg: 'text-amber-700',   label: 'Disputed' },
    written_off: { bg: 'bg-slate-100',  fg: 'text-slate-500',   label: 'Written off' },
  }
  const s = map[status] || map.pending
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.fg}`}>
      {s.label}
    </span>
  )
}

// ─── Sortable table header ──────────────────────────────────────────────────
//
// Click toggles direction when the column is already active; clicking a
// different column makes it the new active sort at DESC. The icon shows
// the current direction for the active column and a neutral icon for the
// rest so the user knows every header is clickable.

function SortableTh({ label, sortKey, active, dir, onSort, align }) {
  const isActive = active === sortKey
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ArrowUpDown
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={[
        'px-4 py-3 text-xs font-semibold uppercase tracking-wider cursor-pointer select-none whitespace-nowrap',
        align === 'right' ? 'text-right' : 'text-left',
        isActive ? 'text-slate-700' : 'text-slate-500 hover:text-slate-700',
      ].join(' ')}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        <Icon size={12} className={isActive ? '' : 'opacity-40'} />
      </span>
    </th>
  )
}


function BundleRow({ bundle, expanded, onToggle, onApproveItem, onApproveBundle, isApproving }) {
  // Compute the date range across the bundle's items so the collapsed
  // row shows when the parcels shipped. Most bundles are same-day; if not,
  // surface the range as "earliest → latest".
  const dates = bundle.items
    .map(i => i.ss_shipped_date)
    .filter(Boolean)
    .sort()
  const earliest = dates[0]
  const latest   = dates[dates.length - 1]
  const dateLabel = !earliest ? null : earliest === latest ? fmtDate(earliest) : `${fmtDate(earliest)} → ${fmtDate(latest)}`

  return (
    <Fragment>
      <tr
        onClick={onToggle}
        className={`hover:bg-slate-50 cursor-pointer transition-colors ${
          bundle.discrepancy_count > 0 ? 'bg-amber-50/30' : ''
        }`}
      >
        <td className="px-4 py-3 text-slate-400">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </td>
        <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
          {dateLabel ?? <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3 font-mono text-xs text-slate-700">
          {bundle.bundle_order_id || (
            <span className="text-slate-400 italic">unmatched ({bundle.items[0]?.tracking_number || '—'})</span>
          )}
          {bundle.matched_count < bundle.items_count && bundle.matched_count > 0 && (
            <span className="ml-2 text-[10px] text-amber-600">
              {bundle.matched_count}/{bundle.items_count} matched
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-slate-600">
          {bundle.clients.length === 0 && '—'}
          {bundle.clients.length === 1 && bundle.clients[0]}
          {bundle.clients.length > 1 && (
            <>
              {bundle.clients[0]}
              <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700"
                    title={bundle.clients.join(', ')}>
                {bundle.clients.length} clients
              </span>
            </>
          )}
        </td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-600">{bundle.items_count}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-700 font-medium">{fmt$(bundle.ss_total)}</td>
        <td className="px-4 py-3 text-right tabular-nums text-slate-600">
          {bundle.matched_count > 0 ? fmt$(bundle.ap_total) : <span className="text-slate-400">—</span>}
        </td>
        <td className={`px-4 py-3 text-right tabular-nums font-semibold ${
          bundle.variance_total > 0.01 ? 'text-red-600' :
          bundle.variance_total < -0.01 ? 'text-emerald-600' :
          'text-slate-500'
        }`}>
          {bundle.discrepancy_count > 0 && <Flag size={11} className="inline mr-1 text-amber-500" />}
          {bundle.matched_count > 0 ? fmt$(bundle.variance_total) : <span className="text-slate-400">—</span>}
        </td>
        <td className="px-4 py-3">
          {bundle.pending_review_count > 0 ? (
            <button
              onClick={(e) => { e.stopPropagation(); onApproveBundle(bundle) }}
              disabled={isApproving}
              className="text-xs px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100 inline-flex items-center gap-1"
              title="Mark all pending discrepancies in this bundle as approved"
            >
              <CheckCircle2 size={11} /> Approve {bundle.pending_review_count}
            </button>
          ) : bundle.discrepancy_count > 0 ? (
            <span className="text-xs text-slate-500">all reviewed</span>
          ) : (
            <span className="text-xs text-emerald-600">✓ no issues</span>
          )}
        </td>
      </tr>

      {expanded && bundle.items.map(item => (
        <tr key={item.order_id} className={`bg-slate-50/40 ${item.has_discrepancy ? 'bg-amber-50/20' : ''}`}>
          <td></td>
          <td className="px-4 py-2 text-xs text-slate-600 whitespace-nowrap">
            {item.ss_shipped_date ? fmtDate(item.ss_shipped_date) : <span className="text-slate-400">—</span>}
          </td>
          <td className="px-4 py-2 pl-10 text-xs">
            <div className="font-mono text-slate-700">
              {item.order_number || item.tracking_number || '—'}
            </div>
            {item.tracking_number && (
              <div className="text-[10px] text-slate-400 font-mono truncate max-w-[24ch]" title={item.tracking_number}>
                {item.tracking_number}
              </div>
            )}
            {item.product_name && (
              <div className="text-[10px] text-slate-500 mt-0.5">
                {item.product_name}{item.movement_type === 'RETURN' && ' (return)'}
              </div>
            )}
            {item.sibling_article_ids?.length > 0 && (
              <div
                className="text-[10px] text-slate-500 mt-1 italic"
                title={item.sibling_article_ids.join('\n')}
              >
                + {item.sibling_article_ids.length} sibling parcel{item.sibling_article_ids.length === 1 ? '' : 's'}
                {' '}({item.parcels_in_consignment} of consignment)
              </div>
            )}
          </td>
          <td className="px-4 py-2 text-xs text-slate-600">
            {item.client_name || <span className="text-slate-400">—</span>}
          </td>
          <td></td>
          <td className="px-4 py-2 text-right tabular-nums text-xs text-slate-700">
            {fmt$(item.ss_carrier_cost)}
          </td>
          <td className="px-4 py-2 text-right tabular-nums text-xs">
            {item.ap_cost_in_gst != null ? (
              <span className="text-slate-700">{fmt$(item.ap_cost_in_gst)}</span>
            ) : (
              <span className="text-slate-400 italic">no AP data</span>
            )}
          </td>
          <td className={`px-4 py-2 text-right tabular-nums text-xs font-medium ${
            (item.variance ?? 0) > 0.01 ? 'text-red-600' :
            (item.variance ?? 0) < -0.01 ? 'text-emerald-600' :
            'text-slate-400'
          }`}>
            {item.variance != null && Math.abs(item.variance) > 0.005 ? fmt$(item.variance) : '—'}
          </td>
          <td className="px-4 py-2">
            <div className="flex items-center gap-1.5">
              {item.has_discrepancy && item.review_status === 'pending' ? (
                <>
                  <button
                    onClick={() => onApproveItem(item, 'approved')}
                    disabled={isApproving}
                    className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => onApproveItem(item, 'disputed')}
                    disabled={isApproving}
                    className="text-[10px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100"
                  >
                    Dispute
                  </button>
                </>
              ) : (
                reviewBadge(item.review_status)
              )}
            </div>
          </td>
        </tr>
      ))}
    </Fragment>
  )
}


// ─── Main page ───────────────────────────────────────────────────────────────

export default function AuspostReconciliation() {
  // Two independent state stores:
  //   syncPeriod  — only drives the manual "Sync now" call (which AP API
  //                 window to pull). Has no effect on the table.
  //   tableFilter — drives the bundles query (what's shown in the table).
  //                 Separate because pulling a wide window from the API and
  //                 viewing a narrow window in the UI are unrelated tasks.
  const [syncPeriod, setSyncPeriod]   = useState(defaultPeriod())
  const [tableFilter, setTableFilter] = useState(() => ({
    ...defaultPeriod(),
    client_id: '',   // '' == all clients
  }))
  const [showOnly, setShowOnly] = useState('discrepancies')
  const [expanded, setExpanded] = useState(() => new Set())
  const queryClient = useQueryClient()
  const { toasts, addToast, removeToast } = useToast()

  const params = useMemo(() => {
    const p = {
      from: tableFilter.from,
      to:   tableFilter.to,
      show_only: showOnly,
    }
    if (tableFilter.client_id) p.client_id = tableFilter.client_id
    return p
  }, [tableFilter, showOnly])

  const { data: summary, isLoading } = useQuery({
    queryKey: ['auspost', 'bundles', params],
    queryFn: () => getAuspostBundles(params),
    refetchInterval: 60_000,
  })

  // Client list for the dropdown (cached across page renders).
  const { data: clientsData } = useQuery({
    queryKey: ['clients', 'all-for-filter'],
    queryFn:  () => getAdminClients(),
    staleTime: 5 * 60_000,
  })
  const clients = clientsData?.clients || clientsData || []

  const itemReviewMut = useMutation({
    mutationFn: ({ articleId, status, notes }) =>
      updateAuspostItemReview(articleId, { status, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auspost', 'bundles'] })
      addToast({ type: 'success', message: 'Review updated' })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Failed to update review',
    }),
  })

  const bundleApproveMut = useMutation({
    mutationFn: (bundle) => approveAuspostBundle({
      article_ids: bundle.items
        .filter(it => it.has_discrepancy && it.review_status === 'pending' && it.article_id)
        .map(it => it.article_id),
      status: 'approved',
    }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['auspost', 'bundles'] })
      addToast({ type: 'success', message: `Approved ${data.updated} item${data.updated !== 1 ? 's' : ''}` })
    },
    onError: (err) => addToast({
      type: 'error',
      message: err.response?.data?.detail || 'Bulk approve failed',
    }),
  })

  const toggle = (key) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const bundles = summary?.bundles || []

  // ── Sorting ────────────────────────────────────────────────────────
  // Click a column header to sort by it. Clicking the active column flips
  // direction. Default 'variance' DESC matches the order the backend
  // returns (largest |variance| first), so the table looks the same on
  // first load.
  const [sortKey, setSortKey] = useState('variance')
  const [sortDir, setSortDir] = useState('desc')

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir('desc')
    }
  }

  const sortedBundles = useMemo(() => {
    const earliestOf = (b) => {
      const dates = b.items.map(i => i.ss_shipped_date).filter(Boolean).sort()
      return dates[0] || ''
    }
    const accessors = {
      shipped:  earliestOf,
      bundle:   (b) => b.bundle_order_id || (b.items[0]?.tracking_number || ''),
      client:   (b) => (b.clients[0] || '').toLowerCase(),
      items:    (b) => b.items_count,
      ss_total: (b) => b.ss_total,
      ap_total: (b) => b.ap_total,
      variance: (b) => Math.abs(b.variance_total),
    }
    const get = accessors[sortKey] || accessors.variance
    const sign = sortDir === 'asc' ? 1 : -1
    const out = [...bundles].sort((a, b) => {
      const va = get(a), vb = get(b)
      if (va === vb) return 0
      // Push empty-string / null to the bottom regardless of direction.
      if (va === '' || va == null) return 1
      if (vb === '' || vb == null) return -1
      return va < vb ? -1 * sign : 1 * sign
    })
    return out
  }, [bundles, sortKey, sortDir])

  // ── Pagination ─────────────────────────────────────────────────────
  // Large bundle counts (hundreds of bundles, each expandable to many
  // sub-rows) chew up render time. Slice client-side and let the user
  // pick a wider page when they want to scan the whole list.
  const [pageSize, setPageSize] = useState(20)
  const [page, setPage]         = useState(1)
  const totalBundles = sortedBundles.length
  const totalPages   = Math.max(1, Math.ceil(totalBundles / pageSize))
  // Reset to page 1 whenever the filtered/sorted set or page size changes
  // — otherwise switching from "all" back to "discrepancies" can leave
  // the user on a page that no longer exists.
  useEffect(() => { setPage(1) }, [totalBundles, pageSize, showOnly, tableFilter.from, tableFilter.to, tableFilter.client_id, sortKey, sortDir])
  const pageStart = (page - 1) * pageSize
  const pageEnd   = Math.min(pageStart + pageSize, totalBundles)
  const pagedBundles = sortedBundles.slice(pageStart, pageEnd)

  return (
    <div className="space-y-6">
      <ToastContainer toasts={toasts} onClose={removeToast} />

      <div>
        <h2 className="text-xl font-semibold text-slate-800">AusPost Reconciliation</h2>
        <p className="text-sm text-slate-500 mt-0.5">
          ShipStation AusPost orders matched to the AusPost API, grouped by bundle.
          Discrepancies are highlighted; approve them in bulk per bundle or per item.
        </p>
      </div>

      <AuspostTabs />

      <WorkerStatusCard />
      <SyncCard period={syncPeriod} setPeriod={setSyncPeriod} />

      <FilterChips showOnly={showOnly} setShowOnly={setShowOnly} summary={summary} />
      <StatTiles summary={summary} />

      <div className="card overflow-hidden">
        <TableFilters
          tableFilter={tableFilter}
          setTableFilter={setTableFilter}
          clients={clients}
        />
        {isLoading ? (
          <div className="flex items-center justify-center py-16 gap-2 text-slate-400">
            <Loader2 size={18} className="animate-spin" />
            <span className="text-sm">Loading…</span>
          </div>
        ) : bundles.length === 0 ? (
          <div className={`flex flex-col items-center justify-center py-16 gap-2 ${
            showOnly === 'discrepancies' ? 'bg-emerald-50/30' : ''
          }`}>
            {showOnly === 'discrepancies' ? (
              <>
                <CheckCircle2 size={32} className="text-emerald-500" />
                <p className="text-sm font-medium text-emerald-800">No discrepancies</p>
                <p className="text-xs text-emerald-600 max-w-md text-center">
                  Every AusPost order in this window matched the API total within tolerance.
                  Click "All orders" to see the full list.
                </p>
              </>
            ) : (
              <>
                <Truck size={32} className="text-slate-300" />
                <p className="text-sm font-medium text-slate-600">No bundles to show</p>
                <p className="text-xs text-slate-400">Try a wider date range or different filter.</p>
              </>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left">
                  <th className="px-4 py-3 w-8"></th>
                  <SortableTh label="Shipped"               sortKey="shipped"  active={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Bundle / Consignment"  sortKey="bundle"   active={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Client"                sortKey="client"   active={sortKey} dir={sortDir} onSort={handleSort} />
                  <SortableTh label="Items"                 sortKey="items"    active={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                  <SortableTh label="ShipStation Σ"         sortKey="ss_total" active={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                  <SortableTh label="AusPost Σ"             sortKey="ap_total" active={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                  <SortableTh label="Variance Σ"            sortKey="variance" active={sortKey} dir={sortDir} onSort={handleSort} align="right" />
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pagedBundles.map(bundle => {
                  const key = bundle.bundle_order_id || bundle.items[0]?.tracking_number || bundle.items[0]?.order_id
                  return (
                    <BundleRow
                      key={key}
                      bundle={bundle}
                      expanded={expanded.has(key)}
                      onToggle={() => toggle(key)}
                      onApproveItem={(item, status) =>
                        itemReviewMut.mutate({ articleId: item.article_id, status })}
                      onApproveBundle={(b) => bundleApproveMut.mutate(b)}
                      isApproving={itemReviewMut.isPending || bundleApproveMut.isPending}
                    />
                  )
                })}
              </tbody>
            </table>

            <PaginationFooter
              page={page}
              setPage={setPage}
              pageSize={pageSize}
              setPageSize={setPageSize}
              totalPages={totalPages}
              totalBundles={totalBundles}
              pageStart={pageStart}
              pageEnd={pageEnd}
            />
          </div>
        )}
      </div>
    </div>
  )
}


// ─── Pagination footer ──────────────────────────────────────────────────────
//
// Page-size selector on the left (default 20 bundles per page, with the
// option to widen to 100/200/500 when scanning the whole list). Page info
// + first/prev/next/last controls on the right.

function PaginationFooter({
  page, setPage, pageSize, setPageSize, totalPages, totalBundles, pageStart, pageEnd,
}) {
  const SIZES = [20, 50, 100, 200, 500]
  if (totalBundles === 0) return null
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-slate-50 text-xs text-slate-600">
      <div className="flex items-center gap-2">
        <span className="text-slate-500">Bundles per page</span>
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          className="px-2 py-1 border border-slate-300 rounded bg-white text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          {SIZES.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <span className="text-slate-500 hidden sm:inline">
          Showing {pageStart + 1}–{pageEnd} of {totalBundles}
        </span>
      </div>

      <div className="flex items-center gap-1 ml-auto">
        <span className="mr-2 text-slate-500 sm:hidden">
          {pageStart + 1}–{pageEnd} of {totalBundles}
        </span>
        <PagerBtn onClick={() => setPage(1)} disabled={page <= 1} aria-label="First page">
          <ChevronsLeft size={14} />
        </PagerBtn>
        <PagerBtn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1} aria-label="Previous page">
          <ChevronLeft size={14} />
        </PagerBtn>
        <span className="px-2 text-slate-700 tabular-nums">
          Page <span className="font-semibold">{page}</span> / {totalPages}
        </span>
        <PagerBtn onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label="Next page">
          <ChevronRight size={14} />
        </PagerBtn>
        <PagerBtn onClick={() => setPage(totalPages)} disabled={page >= totalPages} aria-label="Last page">
          <ChevronsRight size={14} />
        </PagerBtn>
      </div>
    </div>
  )
}

function PagerBtn({ children, onClick, disabled, ...rest }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="p-1.5 rounded border border-slate-300 bg-white text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white"
      {...rest}
    >
      {children}
    </button>
  )
}
